import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ChevronRight,
    Film,
    Shield,
    Gem,
    Scale,
    Lock,
    Feather,
    Upload,
    PenLine,
    CreditCard,
    Plus,
    Minus,
} from "lucide-react";
import Navbar from "../components/Navbar";
import HeroCarousel from "../components/HeroCarousel";
import WorldMap from "../components/WorldMap";
import { useAuth } from "../context/AuthContext";

const HERO_BG_DEPRECATED = null;

const ROLES = [
    {
        role: "creator",
        title: "For Creators",
        kicker: "Assign & monetise",
        copy: "Deliver your master, sign a digital assignment deed and list your film on a vetted global exchange. Get paid once, worldwide.",
        icon: Film,
        cta: "Learn more",
        href: "/register?role=creator",
    },
    {
        role: "buyer",
        title: "For Buyers",
        kicker: "Acquire outright",
        copy: "Browse a curated catalog and acquire worldwide, perpetual copyright through a precision legal workflow. No licences, no renewals.",
        icon: Gem,
        cta: "Learn more",
        href: "/register?role=buyer",
    },
];

const PILLARS = [
    {
        icon: Scale,
        title: "Indian Copyright Act, 1957",
        body: "Every deed follows Section 14 assignment language with exclusive Indian jurisdiction.",
    },
    {
        icon: Shield,
        title: "Chain-of-Title Vetted",
        body: "Writers, music, and talent releases — every underlying right is reviewed before listing.",
    },
    {
        icon: Lock,
        title: "Secure Vault Delivery",
        body: "Post-payment, masters unlock via one-time download with an audit trail.",
    },
    {
        icon: Feather,
        title: "Digital E-sign",
        body: "Creator & buyer signatures are timestamped and stored with the contract.",
    },
];

const STEPS = [
    {
        icon: Upload,
        title: "Creator uploads",
        copy: "Filmmaker submits poster, trailer, master and full metadata to the Studio.",
    },
    {
        icon: PenLine,
        title: "Assignment deed signed",
        copy: "Creator signs a Section 18 deed assigning copyright to Silver Screen Xchange for onward transfer.",
    },
    {
        icon: Shield,
        title: "Legal review",
        copy: "Our team audits chain-of-title, warranties and clearances before the film is listed.",
    },
    {
        icon: Gem,
        title: "Buyer acquires",
        copy: "Verified buyer opens a Deal Room, signs the purchase agreement and pays in escrow.",
    },
    {
        icon: CreditCard,
        title: "Payment captured",
        copy: "Funds are released to the creator. Transfer Record is issued to the buyer.",
    },
    {
        icon: Lock,
        title: "Vault unlocks",
        copy: "Buyer downloads the master, receives chain-of-title archive and proof of ownership.",
    },
];

const METRICS = [
    { v: "50+", label: "Countries represented" },
    { v: "28", label: "Cinema industries" },
    { v: "100%", label: "Exclusive transfers" },
    { v: "1957", label: "Governing Act" },
];

const FAQ = [
    {
        q: "What exactly am I buying on Silver Screen Xchange?",
        a: "You are acquiring the full, exclusive, worldwide copyright to a film — not a licence. Under the Indian Copyright Act, 1957 the assignment is perpetual and irrevocable once the consideration is paid.",
    },
    {
        q: "How is this different from a traditional distribution deal?",
        a: "Distribution deals grant limited rights in a territory for a limited term. Silver Screen Xchange transfers the underlying copyright itself. The buyer becomes the new owner of the work and can license it as they wish.",
    },
    {
        q: "Is the chain-of-title really verified?",
        a: "Yes. Every film submitted is audited for writer, music, performer and producer clearances before it is allowed to list. If a film cannot prove clean title, it is rejected.",
    },
    {
        q: "What happens after payment?",
        a: "A Transfer Record is generated, funds are released to the creator, and the buyer's Vault unlocks with the digital master, chain-of-title archive, and the fully signed Purchase Agreement.",
    },
    {
        q: "Who can list a film?",
        a: "Any producer, studio, or filmmaker who owns the underlying copyright and can demonstrate clean chain-of-title. Signup is free and listing goes through a short review.",
    },
    {
        q: "Can I preview a film before buying?",
        a: "Yes. Every listing shows trailer, synopsis, cast & crew, genre, language, runtime, and chain-of-title summary. Full master access is unlocked only after a completed purchase.",
    },
];

export default function Landing() {
    const { user } = useAuth();
    const catalogHref = user ? "/catalog" : "/login?next=/catalog";

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />

            {/* HERO */}
            <section className="relative overflow-hidden">
                <HeroCarousel />

                <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-10 pt-20 md:pt-36 pb-24 md:pb-44">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="max-w-3xl"
                    >
                        <div className="flex items-center gap-2 text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-platinum mb-5 md:mb-6">
                            <div className="w-8 md:w-10 h-px bg-platinum" />
                            The Cinema Ownership Exchange
                        </div>
                        <h1 className="font-display text-[2.75rem] sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95]">
                            Own the film.
                            <br />
                            <span className="silver-text italic">Not just a licence.</span>
                        </h1>
                        <p className="mt-6 md:mt-8 text-silver-300 text-sm md:text-base lg:text-lg max-w-xl leading-relaxed">
                            Silver Screen Xchange is a marketplace for the exclusive transfer of
                            film copyright under the Indian Copyright Act, 1957. Creators
                            list. Buyers acquire. Ownership moves cleanly — and forever.
                        </p>

                        <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-3 md:gap-4">
                            <Link
                                to="/catalog"
                                className="group inline-flex items-center gap-2 bg-platinum hover:bg-platinum-200 text-ink-300 font-medium px-5 md:px-6 py-3 md:py-3.5 rounded-sm transition-colors text-sm md:text-base"
                                data-testid="hero-browse-catalog"
                            >
                                Browse the Marketplace
                                <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                            </Link>
                            {!user && (
                                <>
                                    <Link
                                        to="/register?role=creator"
                                        className="inline-flex items-center gap-2 border border-platinum/40 text-platinum hover:bg-platinum/10 px-5 py-3 rounded-sm text-xs md:text-sm tracking-[0.2em] uppercase transition-colors"
                                        data-testid="hero-cta-creator"
                                    >
                                        I'm a Creator → list my film
                                    </Link>
                                    <Link
                                        to="/register?role=buyer"
                                        className="text-xs md:text-sm tracking-[0.2em] uppercase text-silver-200 hover:text-white border-b border-white/20 hover:border-platinum pb-1 transition-colors"
                                        data-testid="hero-cta-buyer"
                                    >
                                        I'm a Buyer → register
                                    </Link>
                                </>
                            )}
                        </div>
                    </motion.div>
                </div>

                {/* global cinema ticker */}
                <div className="relative z-10 border-y border-white/5 py-5 overflow-hidden">
                    <div className="flex gap-12 md:gap-16 whitespace-nowrap animate-marquee w-max">
                        {[...Array(2)].map((_, k) => (
                            <div key={k} className="flex gap-12 md:gap-16">
                                {[
                                    "Hollywood",
                                    "Bollywood",
                                    "Nollywood",
                                    "K-Cinema",
                                    "Cinéma Français",
                                    "J-Cinema",
                                    "Cinema Italiano",
                                    "Deutsches Kino",
                                    "Cine Mexicano",
                                    "Cine Argentino",
                                    "Cinema Brasileiro",
                                    "İran Sineması",
                                    "Türk Sineması",
                                    "Cine Español",
                                    "中国电影",
                                    "Hong Kong New Wave",
                                    "Tollywood",
                                    "Kollywood",
                                    "Mollywood",
                                    "Pinoy Cinema",
                                    "Lollywood",
                                    "Thai Cinema",
                                    "Indonesian Cinema",
                                    "Nordic Noir",
                                    "Czech New Wave",
                                    "Israeli Cinema",
                                    "Egyptian Cinema",
                                    "South African Cinema",
                                    "Cannes",
                                    "Berlinale",
                                    "Venice",
                                    "Sundance",
                                    "TIFF",
                                    "Busan",
                                    "IFFI Goa",
                                    "Locarno",
                                    "Rotterdam",
                                    "Tribeca",
                                ].map((t) => (
                                    <span
                                        key={t + k}
                                        className="font-display italic text-xl md:text-3xl text-silver-300/60"
                                    >
                                        {t}
                                    </span>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHAT IS SILVERRIGHTS */}
            <section className="max-w-[1400px] mx-auto px-4 md:px-10 py-16 md:py-24" data-testid="section-about">
                <div className="grid md:grid-cols-[1fr_1.4fr] gap-8 md:gap-16">
                    <div>
                        <div className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-platinum mb-3">
                            What is Silver Screen Xchange
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl leading-tight">
                            A <span className="silver-text italic">clean</span> way to
                            buy and sell <br className="hidden md:block" /> film copyright.
                        </h2>
                    </div>
                    <div className="space-y-4 text-silver-300 leading-relaxed text-sm md:text-base max-w-2xl">
                        <p>
                            For decades, film rights have moved through private treaties,
                            handshake deals, and opaque distribution paper. Silver Screen Xchange
                            brings that transfer into the open — as a proper exchange where
                            creators and buyers can meet, negotiate, and close ownership
                            transfers with full legal clarity.
                        </p>
                        <p>
                            We operate under Indian law. Every film on the platform is
                            vetted for chain-of-title. Every deal is captured as a signed,
                            timestamped Purchase Agreement. And every transfer ends in a
                            secure Vault hand-over to the new owner.
                        </p>
                    </div>
                </div>
            </section>

            {/* WORLDWIDE REACH */}
            <section className="bg-ink-200/30 border-y border-white/5">
                <div className="max-w-[1500px] mx-auto px-4 md:px-10 py-16 md:py-24" data-testid="section-map">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                        <div>
                            <div className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-platinum mb-3">
                                Worldwide reach
                            </div>
                            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl leading-tight max-w-2xl">
                                50+ countries.{" "}
                                <span className="silver-text italic">One exchange.</span>
                            </h2>
                        </div>
                        <p className="max-w-sm text-silver-400 text-sm leading-relaxed">
                            From Hollywood to Busan, Mumbai to Lagos, Paris to Tokyo — films
                            from every major cinema industry pass through Silver Screen Xchange.
                        </p>
                    </div>

                    <WorldMap />

                    <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-[10px] md:text-xs tracking-[0.2em] uppercase text-silver-500">
                        <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-white shadow-[0_0_10px_rgba(229,228,226,0.6)]" />
                            Major cinema hubs
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-silver-200/80" />
                            Industries & festivals represented
                        </div>
                    </div>
                </div>
            </section>
            <section className="bg-ink-200/40 border-y border-white/5">
                <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-16 md:py-24">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
                        <div>
                            <div className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-platinum mb-2 md:mb-3">
                                How it works
                            </div>
                            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl max-w-xl leading-tight">
                                From master file to new owner in{" "}
                                <span className="silver-text italic">six clean steps.</span>
                            </h2>
                        </div>
                        <p className="text-silver-400 max-w-sm text-sm">
                            No middlemen, no renewal fees. A transparent legal and financial
                            workflow built for cinema.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                        {STEPS.map((s, i) => (
                            <motion.div
                                key={s.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.45, delay: i * 0.08 }}
                                className="glass p-6 rounded-sm relative"
                                data-testid={`step-${i + 1}`}
                            >
                                <div className="absolute top-5 right-5 font-display text-3xl text-silver-500/40">
                                    0{i + 1}
                                </div>
                                <s.icon className="w-6 h-6 text-platinum mb-4" />
                                <div className="font-display text-xl md:text-2xl mb-2">
                                    {s.title}
                                </div>
                                <p className="text-silver-400 text-sm leading-relaxed">
                                    {s.copy}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* METRICS */}
            <section className="max-w-[1400px] mx-auto px-4 md:px-10 py-14 md:py-20">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {METRICS.map((m) => (
                        <div key={m.label} className="text-center md:text-left">
                            <div className="font-display text-4xl md:text-6xl silver-text">
                                {m.v}
                            </div>
                            <div className="text-silver-500 text-[10px] md:text-xs uppercase tracking-[0.3em] mt-2">
                                {m.label}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ROLE SELECTOR */}
            <section className="max-w-[1400px] mx-auto px-4 md:px-10 py-10 md:py-16">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-10">
                    <div>
                        <div className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-platinum mb-2 md:mb-3">
                            Who is it for
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl max-w-xl leading-tight">
                            Two sides of the <span className="platinum-text">deal table.</span>
                        </h2>
                    </div>
                    <p className="hidden md:block max-w-sm text-silver-400 text-sm">
                        A single platform for the creators who build worlds and the buyers
                        who take them global.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-5 md:gap-8">
                    {ROLES.map((r, i) => (
                        <motion.div
                            key={r.role}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            whileHover={{ y: -6 }}
                            className="glass p-6 md:p-10 rounded-sm relative overflow-hidden group"
                            data-testid={`role-card-${r.role}`}
                        >
                            <div className="absolute -top-24 -right-24 w-64 h-64 bg-platinum/10 blur-3xl group-hover:bg-platinum/20 transition-colors" />
                            <r.icon className="w-7 h-7 md:w-8 md:h-8 text-platinum mb-5 md:mb-6" />
                            <div className="text-[10px] tracking-[0.3em] uppercase text-silver-400 mb-2">
                                {r.kicker}
                            </div>
                            <h3 className="font-display text-2xl md:text-4xl mb-2 md:mb-3">
                                {r.title}
                            </h3>
                            <p className="text-silver-300 max-w-md leading-relaxed text-sm md:text-base">
                                {r.copy}
                            </p>
                            <Link
                                to={r.href}
                                className="inline-flex items-center gap-2 mt-6 md:mt-8 text-platinum font-medium border-b border-platinum/40 hover:border-platinum pb-1 text-sm md:text-base"
                                data-testid={`role-cta-${r.role}`}
                            >
                                {r.cta}
                                <ChevronRight className="w-4 h-4" />
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* PILLARS */}
            <section className="max-w-[1400px] mx-auto px-4 md:px-10 pb-16 md:pb-24">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                    {PILLARS.map((p, i) => (
                        <motion.div
                            key={p.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.08 }}
                            className="p-5 md:p-6 border border-white/5 rounded-sm bg-ink-200/40"
                        >
                            <p.icon className="w-5 h-5 text-platinum mb-3 md:mb-4" />
                            <div className="font-display text-base md:text-lg mb-1.5 md:mb-2">
                                {p.title}
                            </div>
                            <p className="text-silver-400 text-xs md:text-sm leading-relaxed">
                                {p.body}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* LEGAL FRAMEWORK */}
            <section className="bg-ink-200/40 border-y border-white/5">
                <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-16 md:py-24 grid md:grid-cols-[1fr_1.2fr] gap-10 md:gap-16 items-start">
                    <div>
                        <div className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-platinum mb-3 flex items-center gap-2">
                            <Scale className="w-4 h-4" />
                            Legal framework
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl leading-tight">
                            Built on the{" "}
                            <span className="silver-text italic">Indian Copyright Act, 1957.</span>
                        </h2>
                        <p className="text-silver-400 mt-5 text-sm md:text-base max-w-md leading-relaxed">
                            Our contracts use Section 14 rights language, Section 18
                            assignment mechanics, and exclusive Indian jurisdiction — the
                            same legal spine that protects every Indian cinematic work.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {[
                            {
                                title: "Section 14",
                                body: "Defines the exclusive economic rights — reproduction, communication, adaptation, translation — which we transfer in whole.",
                            },
                            {
                                title: "Section 18",
                                body: "Governs the assignment of copyright. Our Deed follows the required form: work, rights assigned, territory, term.",
                            },
                            {
                                title: "Warranties",
                                body: "Sole ownership, no infringement, no prior assignment, complete chain-of-title — each explicitly warranted by the creator.",
                            },
                            {
                                title: "Indemnity",
                                body: "A seven-year indemnity window is extended by Silver Screen Xchange and the creator to every verified buyer.",
                            },
                        ].map((x) => (
                            <div key={x.title} className="glass p-5 rounded-sm">
                                <div className="text-platinum text-[10px] tracking-[0.3em] uppercase mb-2">
                                    {x.title}
                                </div>
                                <p className="text-silver-300 text-sm leading-relaxed">
                                    {x.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="max-w-[1100px] mx-auto px-4 md:px-10 py-16 md:py-24" data-testid="section-faq">
                <div className="mb-10 md:mb-12">
                    <div className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-platinum mb-3">
                        Questions
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl md:text-5xl leading-tight">
                        Straight answers, no <span className="silver-text italic">small print.</span>
                    </h2>
                </div>
                <FAQList />
            </section>

            {/* CTA */}
            <section className="relative border-t border-white/5 py-16 md:py-20">
                <div className="max-w-[1200px] mx-auto px-4 md:px-6 text-center">
                    <div className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-platinum mb-3 md:mb-4">
                        Ready when you are
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl md:text-6xl leading-tight max-w-3xl mx-auto">
                        Start buying or selling film copyright today.
                    </h2>
                    <div className="mt-8 md:mt-10 flex flex-wrap justify-center gap-3 md:gap-4">
                        <Link
                            to="/catalog"
                            className="inline-flex items-center gap-2 bg-platinum hover:bg-platinum-200 text-ink-300 font-medium px-6 md:px-8 py-3 md:py-4 rounded-sm text-sm md:text-base"
                            data-testid="cta-enter-catalog"
                        >
                            Browse the Marketplace <ChevronRight className="w-4 h-4" />
                        </Link>
                        {!user && (
                            <>
                                <Link
                                    to="/register?role=creator"
                                    className="inline-flex items-center gap-2 border border-platinum/40 text-platinum hover:bg-platinum/10 font-medium px-6 md:px-8 py-3 md:py-4 rounded-sm text-sm md:text-base"
                                    data-testid="cta-register-creator"
                                >
                                    Join as Creator
                                </Link>
                                <Link
                                    to="/register?role=buyer"
                                    className="inline-flex items-center gap-2 border border-white/15 hover:border-platinum text-silver-200 hover:text-white font-medium px-6 md:px-8 py-3 md:py-4 rounded-sm text-sm md:text-base"
                                    data-testid="cta-register-buyer"
                                >
                                    Join as Buyer
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </section>

            <footer className="border-t border-white/5 py-8 text-center text-xs text-silver-500 px-4">
                © {new Date().getFullYear()} Silver Screen Xchange — Exchange operated under the
                Indian Copyright Act, 1957.
            </footer>
        </div>
    );
}

function FAQList() {
    const [open, setOpen] = useState(0);
    return (
        <div className="divide-y divide-white/5 border-y border-white/5">
            {FAQ.map((item, i) => {
                const isOpen = open === i;
                return (
                    <div key={item.q} className="py-4 md:py-5">
                        <button
                            onClick={() => setOpen(isOpen ? -1 : i)}
                            className="w-full flex items-center justify-between gap-4 text-left"
                            data-testid={`faq-toggle-${i}`}
                        >
                            <span className="font-display text-lg md:text-xl text-white">
                                {item.q}
                            </span>
                            <span className="w-8 h-8 shrink-0 rounded-full border border-white/15 flex items-center justify-center text-silver-300">
                                {isOpen ? (
                                    <Minus className="w-4 h-4" />
                                ) : (
                                    <Plus className="w-4 h-4" />
                                )}
                            </span>
                        </button>
                        <motion.div
                            initial={false}
                            animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                        >
                            <p className="text-silver-400 mt-3 md:mt-4 text-sm md:text-base leading-relaxed max-w-3xl">
                                {item.a}
                            </p>
                        </motion.div>
                    </div>
                );
            })}
        </div>
    );
}
